import React from 'react';
import { motion } from 'framer-motion';
import Avatar from '../common/Avatar';
import { useLanguage } from '../../context/LanguageContext';
import { LangGraphMessage } from '../../types';

interface ProgressIndicatorProps {
  messages: LangGraphMessage[];
  className?: string;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ messages, className = '' }) => {
  const { labels } = useLanguage();

  // Determine which agent is currently processing based on the message stream
  const getCurrentAgent = () => {
    if (messages.length === 0) return 'agent'; // Default fallback
    
    // Look through messages starting from the latest to find tool calls that indicate agent transfers
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      
      // Check if this message has tool calls
      if (message.tool_calls?.length > 0) {
        const toolNames = message.tool_calls.map((toolCall: any) => toolCall.name);
        
        // Check for specific transfer tool calls to determine which agent is currently processing
        if (toolNames.includes('transfer_to_planning_agent')) {
          return 'planning_agent';
        }
        if (toolNames.includes('transfer_to_research_agent')) {
          return 'research_agent';
        }
        if (toolNames.includes('transfer_back_to_supervisor')) {
          return 'supervisor';
        }
        if (toolNames.includes('transfer_to_query_storage_agent')) {
          return 'query_storage_agent';
        }
      }
    }
    
    // If no transfer tool calls found, check if the last message was from user
    // and assume the main agent is processing
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.type === 'human') {
      return 'agent';
    }
    
    return 'agent'; // Default fallback
  };

  const currentAgent = getCurrentAgent();

  // Get agent status message
  const getAgentStatus = () => {
    if (currentAgent === 'agent' || currentAgent === 'supervisor') {
      return labels.analyzingRequest;
    }
    if (currentAgent === 'planning_agent') {
      return labels.planningResearch;
    }
    if (currentAgent === 'research_agent') {
      return labels.conductingResearch;
    }
    if (currentAgent === 'query_storage_agent') {
      return labels.storingQuery;
    }

    // For unknown agents, show a generic processing message
    if (currentAgent) {
      return `${currentAgent} ${labels.processing.toLowerCase()}`;
    }
    return labels.processing;
  };

  // Get agent display name
  const getAgentName = () => {
    if (currentAgent === 'planning_agent') return labels.planningAgentRole;
    if (currentAgent === 'research_agent') return labels.researchAgentRole;
    if (currentAgent === 'query_storage_agent') return labels.storageAgentRole;
    if (currentAgent === 'agent' || currentAgent === 'supervisor') return labels.mainAgentRole;
    return currentAgent || labels.agentRole;
  };

  // Get container styling based on agent
  const getContainerClasses = () => {
    const baseClasses = "rounded-lg p-4 max-w-4xl shadow-sm";
    
    // All agents now use the same styling as the main agent
    return `${baseClasses} bg-white border-2 border-primary-200`;
  };

  return (
    <motion.div
      className={`flex mb-6 justify-start ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-col items-start max-w-3xl">
        <div className="flex items-center mb-1 flex-row">
          <Avatar
            name={getAgentName()}
            size="sm"
            className="mr-2"
          />
          <span className="text-sm font-semibold text-primary-700">{getAgentName()}</span>
        </div>

        <div className={getContainerClasses()}>
          <div className="flex items-center">
            <span className="h-2 w-2 bg-primary-500 rounded-full mr-1 animate-pulse"></span>
            <span className="h-2 w-2 bg-primary-500 rounded-full mr-1 animate-pulse" style={{ animationDelay: '0.2s' }}></span>
            <span className="h-2 w-2 bg-primary-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></span>
            <span className="ml-2 text-sm text-primary-500">{getAgentStatus()}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProgressIndicator; 